import React from "react";
import styled from "styled-components";
import {useAtomValue} from "jotai";

import {campaignBasicInfoAtom} from "./entity/Info";
import {campaignBudgetInfoAtom} from "./entity/Budget";
import {campaignGroupInfoAtom} from "./entity/Group";
import {campaignCreativeAtom} from "./entity/Creative";

const STEP_META = [
  {number: 1, label: "기본 정보"},
  {number: 2, label: "예산 · 입찰"},
  {number: 3, label: "게재 · 타겟팅"},
  {number: 4, label: "소재 등록"}
];

const PRODUCT_LABELS = {
  AUDIO: "오디오",
  BANNER: "배너",
  LINK: "링크",
  POP_UNDER: "팝언더"
};

const INVENTORY_LABELS = {
  NONE: "별도 지정 안 함",
  MANUAL: "직접 선택",
  AUTO: "자동 설정"
};

const GENDER_LABELS = {
  MALE: "남성",
  FEMALE: "여성",
  UNKNOWN: "미상"
};

const AGE_LABELS = {
  AGE_10_19: "10대",
  AGE_20_29: "20대",
  AGE_30_39: "30대",
  AGE_40_49: "40대",
  AGE_50_59: "50대",
  AGE_60_PLUS: "60대 이상",
  UNKNOWN: "미상"
};

const formatNumber = value => {
  const numeric = Number(String(value ?? "").replaceAll(",", ""));
  return Number.isFinite(numeric) && numeric > 0
      ? `${numeric.toLocaleString("ko-KR")}원`
      : "미설정";
};

const formatCodeList = (values, labels, fallback = "전체") =>
    Array.isArray(values) && values.length > 0
        ? values.map(value => labels[value] || value).join(", ")
        : fallback;

const getBannerImageCount = creative =>
    (creative?.materials ?? []).reduce(
        (total, material) =>
            total + (Array.isArray(material?.images) ? material.images.length : 0),
        0
    );

const getCreativeSummary = (creative, pendingAudioFile, pendingCompanionImageFile) => {
  const materialType = creative?.creativeType === "POP_UNDER"
      ? "LINK"
      : creative?.creativeType || "AUDIO";
  const commonItems = [
    {label: "소재 유형", value: PRODUCT_LABELS[materialType] || materialType}
  ];

  if (materialType === "BANNER") {
    const imageCount = getBannerImageCount(creative);
    return [
      ...commonItems,
      {label: "등록 파일", value: imageCount > 0 ? `${imageCount}개` : "미등록"},
      {label: "PC 랜딩", value: creative?.pcLandingUrl},
      {label: "모바일 랜딩", value: creative?.mobLandingUrl}
    ];
  }

  if (materialType === "LINK") {
    return [
      ...commonItems,
      {label: "PC 랜딩", value: creative?.pcLandingUrl},
      {label: "모바일 랜딩", value: creative?.mobLandingUrl}
    ];
  }

  return [
    ...commonItems,
    {
      label: "등록 파일",
      value: pendingAudioFile?.name || (creative?.filePath ? "1개" : "미등록")
    },
    {
      label: "보조 이미지",
      value: pendingCompanionImageFile?.name ||
          (creative?.companionImagePath ? "등록" : "미등록")
    },
    {
      label: "보조 이미지 랜딩",
      value: creative?.companionLandingUrl || "미설정"
    }
  ];
};

export default function CampaignSettingSummary({
  currentStep = 0,
  pendingAudioFile = null,
  regionTarget = null,
  pendingCompanionImageFile = null
}) {
  const basic = useAtomValue(campaignBasicInfoAtom);
  const budget = useAtomValue(campaignBudgetInfoAtom);
  const group = useAtomValue(campaignGroupInfoAtom);
  const creative = useAtomValue(campaignCreativeAtom);

  const summaries = [
    [
      {label: "광고주", value: basic?.advertiserName || basic?.username},
      {label: "캠페인명", value: basic?.name},
      {label: "상품", value: PRODUCT_LABELS[basic?.productType] || basic?.productType}
    ],
    [
      {label: "일 예산", value: budget?.infiniteBudgetYn === "Y" ? "제한 없음" : formatNumber(budget?.dailyAvgBudget)},
      {label: "입찰 방식", value: budget?.biddingType},
      {label: "입찰가", value: formatNumber(budget?.biddingPrice)},
      {
        label: "PC / 모바일",
        value: `PC ${formatNumber(budget?.pcBudget)} / 모바일 ${formatNumber(budget?.mobBudget)}`
      }
    ],
    [
      {label: "게재 지면", value: INVENTORY_LABELS[group?.exposureInventoryType] || group?.exposureInventoryType},
      {
        label: "성별",
        value: group?.genderTargetConfigType === "ALL"
            ? "전체"
            : formatCodeList(group?.genders, GENDER_LABELS, "미설정")
      },
      {
        label: "연령",
        value: group?.ageTargetConfigType === "ALL"
            ? "전체"
            : formatCodeList(group?.ageGroups, AGE_LABELS, "미설정")
      },
      {
        label: "지역",
        value: regionTarget?.regionTargetConfigType === "MANUAL"
            ? `${regionTarget?.regions?.length ?? 0}개 지역`
            : "전체"
      }
    ],
    getCreativeSummary(creative, pendingAudioFile, pendingCompanionImageFile)
  ];

  const activeSummary = summaries[currentStep] || summaries[0];
  const completedCount = activeSummary.filter(item =>
      item.value && item.value !== "미설정" && item.value !== "미등록"
  ).length;

  return (
      <SummaryAside aria-label="현재 캠페인 설정 요약">
        <SummaryHeader>
          {/*<HeaderEyebrow>LIVE SUMMARY</HeaderEyebrow>*/}
          <HeaderTitle>현재 설정 정보</HeaderTitle>
          <HeaderDescription>
            입력한 내용이 실시간으로 반영됩니다.
          </HeaderDescription>
        </SummaryHeader>

        {/*<StepList aria-label="캠페인 설정 단계">
          {STEP_META.map((step, index) => (
              <StepItem key={step.number} $active={index === currentStep} $complete={index < currentStep}>
                <StepNumber>{index < currentStep ? "✓" : step.number}</StepNumber>
                <StepLabel>{step.label}</StepLabel>
              </StepItem>
          ))}
        </StepList>*/}

        <CurrentSection>
          <SectionHeading>
            <span>{STEP_META[currentStep]?.label || "설정 정보"}</span>
            <Completion>{completedCount}/{activeSummary.length}</Completion>
          </SectionHeading>

          <SummaryList>
            {activeSummary.map(item => (
                <SummaryRow key={item.label}>
                  <dt>{item.label}</dt>
                  <dd title={item.value || "미설정"}>{item.value || "미설정"}</dd>
                </SummaryRow>
            ))}
          </SummaryList>
        </CurrentSection>

        {/*<SummaryHint>*/}
        {/*  이전 단계로 이동해 값을 변경하면 이 요약도 함께 갱신됩니다.*/}
        {/*</SummaryHint>*/}
      </SummaryAside>
  );
}

const SummaryAside = styled.aside`
  position: sticky;
  top: 18px;
  overflow: hidden;
  border: 1px solid #e6e9ed;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(31, 41, 55, 0.08);
`;

const SummaryHeader = styled.header`
  padding: 22px 20px 20px;
  border-bottom: 1px solid #f0e8e1;
  background: linear-gradient(145deg, #fffaf5 0%, #fff 100%);
`;

const HeaderEyebrow = styled.div`
  margin-bottom: 6px;
  color: #f5811f;
  font-size: var(--font-size-12);
  font-weight: 800;
  letter-spacing: 0.12em;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  color: #242a31;
  font-size: var(--font-size-20);
  letter-spacing: -0.02em;
`;

const HeaderDescription = styled.p`
  margin-top: 7px;
  color: #7b838c;
  font-size: var(--font-size-12);
  line-height: 1.5;
`;

const StepList = styled.ol`
  display: grid;
  gap: 5px;
  margin: 0;
  padding: 16px 18px;
  border-bottom: 1px solid #eceff2;
`;

const StepItem = styled.li`
  display: flex;
  min-height: 34px;
  align-items: center;
  gap: 10px;
  padding: 5px 8px;
  border-radius: 8px;
  background: ${({$active}) => $active ? "#fff5eb" : "transparent"};
  color: ${({$active, $complete}) => $active ? "#d96500" : $complete ? "#4f5963" : "#a0a6ad"};
`;

const StepNumber = styled.span`
  display: inline-flex;
  width: 23px;
  height: 23px;
  flex: 0 0 23px;
  align-items: center;
  justify-content: center;
  border: 1px solid currentColor;
  border-radius: 50%;
  font-size: var(--font-size-12);
  font-weight: 800;
`;

const StepLabel = styled.span`
  font-size: var(--font-size-14);
  font-weight: 700;
`;

const CurrentSection = styled.section`
  padding: 18px 20px 20px;
`;

const SectionHeading = styled.h3`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 14px;
  color: #242a31;
  font-size: var(--font-size-16);
`;

const Completion = styled.small`
  padding: 3px 8px;
  border-radius: 999px;
  background: #f2f4f6;
  color: #737c85;
  font-size: var(--font-size-12);
`;

const SummaryList = styled.dl`
  display: grid;
  gap: 0;
  margin: 0;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px dashed #eceff2;
  font-size: var(--font-size-14);
  line-height: 1.45;

  &:last-child { border-bottom: 0; }
  dt { color: #7b838c; }
  dd {
    overflow: hidden;
    margin: 0;
    color: #30363d;
    font-weight: 700;
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SummaryHint = styled.p`
  margin: 0;
  padding: 15px 20px;
  border-top: 1px solid #eceff2;
  background: #fafbfc;
  color: #858d95;
  font-size: var(--font-size-13);
  line-height: 1.55;
`;
